/**
 * Template budgets for the Plan & budget tab.
 *
 * Owns the record, writes it through on every change, and keeps one step of
 * undo — enough to take back a "fit to plan" or a removal, which are the two
 * changes made with one click and regretted with the next.
 *
 * Other tabs of this browser are followed through the storage event, so two
 * open windows on the same organisation never show different budgets.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { emptyRecord, loadBudgets, saveBudgets, storageKey } from '../../utils/budgetStore'
import { DEFAULT_ALERT_AT, distributeByUsage, fitToPlan } from '../../utils/budgets'

export function useBudgets(orgKey) {
  const [record, setRecord] = useState(() => loadBudgets(orgKey))
  const [undo, setUndo] = useState(null)
  const [saveFailed, setSaveFailed] = useState(false)
  const recordRef = useRef(record)
  recordRef.current = record

  useEffect(() => {
    setRecord(loadBudgets(orgKey))
    setUndo(null)
  }, [orgKey])

  useEffect(() => {
    const key = storageKey(orgKey)
    const onStorage = e => {
      if (e.key === key) setRecord(e.newValue ? loadBudgets(orgKey) : emptyRecord())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [orgKey])

  const commit = useCallback((next, undoLabel = null) => {
    const prev = recordRef.current
    recordRef.current = next
    setRecord(next)
    setSaveFailed(!saveBudgets(orgKey, next))
    setUndo(undoLabel ? { label: undoLabel, record: prev } : null)
  }, [orgKey])

  const setBudget = useCallback((templateId, { amount, alertAt = DEFAULT_ALERT_AT }) => {
    const prev = recordRef.current
    const id = String(templateId)
    const budgets = { ...prev.budgets }
    if (Number(amount) > 0) {
      budgets[id] = { amount: Number(amount), alertAt: Number(alertAt) || DEFAULT_ALERT_AT, updatedAt: new Date().toISOString() }
    } else {
      delete budgets[id]
    }
    commit({ ...prev, budgets })
  }, [commit])

  const removeBudget = useCallback((templateId, name = 'budget') => {
    const prev = recordRef.current
    if (!prev.budgets[String(templateId)]) return
    const budgets = { ...prev.budgets }
    delete budgets[String(templateId)]
    commit({ ...prev, budgets }, `Removed the budget for ${name}`)
  }, [commit])

  const fit = useCallback(pool => {
    const prev = recordRef.current
    const stamp = new Date().toISOString()
    const fitted = fitToPlan(prev.budgets, pool)
    for (const [id, b] of Object.entries(fitted)) {
      if (prev.budgets[id]?.amount !== b.amount) fitted[id] = { ...b, updatedAt: stamp }
    }
    commit({ ...prev, budgets: fitted, overPlanAcknowledged: null }, 'Budgets scaled to fit the plan')
  }, [commit])

  const distribute = useCallback((rows, pool) => {
    const prev = recordRef.current
    const stamp = new Date().toISOString()
    const next = distributeByUsage(rows, pool, prev.budgets)
    for (const [id, b] of Object.entries(next)) next[id] = { ...b, updatedAt: stamp }
    commit({ ...prev, budgets: next, overPlanAcknowledged: null }, 'Plan shared out by this cycle’s usage')
  }, [commit])

  /** "I mean it": the excess is expected overage, not an oversight — for this exact total. */
  const acknowledgeOverPlan = useCallback(total => {
    commit({ ...recordRef.current, overPlanAcknowledged: Number(total) })
  }, [commit])

  const clearAll = useCallback(() => {
    commit({ ...recordRef.current, budgets: {}, overPlanAcknowledged: null }, 'Cleared every budget')
  }, [commit])

  const undoLast = useCallback(() => {
    if (!undo) return
    const restored = undo.record
    recordRef.current = restored
    setRecord(restored)
    setSaveFailed(!saveBudgets(orgKey, restored))
    setUndo(null)
  }, [undo, orgKey])

  return {
    budgets: record.budgets,
    overPlanAcknowledged: record.overPlanAcknowledged,
    setBudget,
    removeBudget,
    fit,
    distribute,
    acknowledgeOverPlan,
    clearAll,
    undo,
    undoLast,
    dismissUndo: () => setUndo(null),
    saveFailed,
  }
}
